import { createFileRoute, useNavigate } from '@tanstack/react-router'
import ActionCard from '@components/ActionCard/ActionCard.component'
import { Box, CircularProgress, Divider, InputAdornment, styled, TextField, Typography } from '@mui/material'
// import { Route as CreateTechnologyRoute } from './_admin.create-technology';
// import { Route as CreateCategoryRoute } from './_admin.create-category';
// import { Route as CreateInterviewRoute } from './_admin.create-interview';
// import { Route as AdminSingleInterviewRoute } from './_admin.admin-single-interview';
import AdjustIcon from '@mui/icons-material/Adjust';
import SearchIcon from 'icons/Search.icon';
import AndroidIcon from 'icons/Technologies/Android.icon';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import NoData from 'icons/NoData.icon';
import reviewStore from 'store/reviewStore';

export const Route = createFileRoute('/(partner)/_partner/created-interviews')({
  component: RouteComponent,
})

interface Interview {
  id: string;
  createdOnUtc: string;
  numberOfCandidates: number;
  technologyName: string;
  interviewName: string;
}

interface InterviewsData {
  [technologyName: string]: Interview[];
}

const StyledSearch = styled(TextField)(() => ({
    input: {
        lineHeight: '20px',
    },
    width: '50%',
    '& .MuiOutlinedInput-notchedOutline': {
        border: '1px solid var(--Interviewer-Lightest-Black)'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        border: '1px solid var(--Interviewer-Black)',
    },
    '&:focused .MuiOutlinedInput-notchedOutline': {
        border: '1px solid var(--Interviewer-Black)',
    },
}));

const CandidateBadge = styled(Typography)(() => ({
  backgroundColor: 'var(--Lightest-Green)',
  color: 'var(--Interviewer-Green)',
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '24px',
  borderRadius: '100px'
}));

function RouteComponent() {

  const navigate = useNavigate();

  const {reviewData, updateReviewData} = reviewStore();
  
  // Fetch interviews data using TanStack Query
  const { data: interviewsData, isLoading, error } = useQuery({
    queryKey: ['created-interviews'],
    queryFn: async (): Promise<InterviewsData> => {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://interviewer.runasp.net/api/interviews/all', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log(response.data);
      return response.data;
    },
  });

  // Helper function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const handleClick = (id:string) => {
    updateReviewData({
      interviewId: id
    })
    navigate({
      to: '/single-interview'
    })
  }

  return (
    <Box
      
    >
        <Box sx={{
        padding: {xs: '16px', md: '32px'}
      }}> 
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: '20px',
          }}>
            <ActionCard
              cardType="technology"
              title="Create a technology"
              description="Click here to create technology"
            //   onClick={() => navigate({ to: CreateTechnologyRoute.to })}
            />
            <ActionCard
              cardType="category"
              title="Create a category"
              description="Click here to create a category for a specific technology"
            //   onClick={() => navigate({ to: CreateCategoryRoute.to, search: { technologyId: undefined, categoryId: undefined } })}
            />
            <ActionCard
              cardType="interview"
              title="Create a interview"
              description="Click here to create an interview"
            //   onClick={() => navigate({ to: CreateInterviewRoute.to, search: { interviewId: undefined } })}
            />
          </Box>
          <Typography variant='h1' sx={{py: '32px'}}>Created Interviews</Typography>
          <Divider 
            orientation="horizontal" sx={{
            borderColor: 'var(--Interviewer-Near-White)',
          }}/>
          <Box sx={{pt: '32px'}}>
              <StyledSearch
                sx={{width: {xs: '100%', lg: '50%'}, backgroundColor: '#fff'}}
                type="search"
                slotProps={{
                input: {
                    startAdornment: (
                    <InputAdornment position="start">
                        <SearchIcon />
                    </InputAdornment>
                    ),
                },
                }}
                placeholder="Search for a technology"
            />
          </Box>
        </Box>

        <Box sx={{
          padding: {xs: '16px', md: '32px'},
          backgroundColor: '#f9f9f9'
        }}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
          )}
          
          {error && (
             <NoData />
          )}
          
          {interviewsData && Object.entries(interviewsData).map(([technologyName, interviews]) => (
            <Box key={technologyName} sx={{ mb: 4 }}>
              <Box sx={{display: 'flex', gap: '16px', pb: '16px'}}>
                <AndroidIcon />
                <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
                  <Typography sx={{fontSize: '12px', color: 'var(--Interviewer-Lightest-Black)'}}>Mobile</Typography>
                  <Typography variant='h2'>{technologyName}</Typography>
                </Box>
              </Box>
              
              {interviews.map((interview) => (
                <Box 
                  key={interview.id} 
                  onClick={() => handleClick(interview.id)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    background: '#fff',
                    borderRadius: '12px',
                    justifyContent: 'space-between',
                    boxShadow: '0px 1px 2px 0px #0205111A',
                    px: '16px',
                    py: '16px',
                    width: '100%',
                    mb: 2,
                    gap: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: '0px 4px 8px 0px #0205112A',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '14px',
                      color: 'var(--Interviewer-Black)',
                      minWidth: '200px',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {interview.interviewName}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minWidth: '150px',
                    }}
                  >
                    <Typography sx={{ color: 'var(--Interviewer-Lightest-Black)', fontWeight: 500, fontSize: '14px' }}>
                      Created on
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', color: 'var(--Interviewer-Black)' }}>
                      {formatDate(interview.createdOnUtc)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      minWidth: '100px',
                    }}
                  >
                    <AdjustIcon sx={{ color: 'var(--Interviewer-Green)', width: '16px', height: '16px' }} />
                    <Typography sx={{ color: 'var(--Interviewer-Green)', fontWeight: 600, fontSize: '12px' }}>
                      Live now!
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      minWidth: '120px',
                    }}
                  >
                    <CandidateBadge sx={{ px: 2, py: 0.5, fontSize: '14px' }}>
                      {interview.numberOfCandidates} candidate{interview.numberOfCandidates !== 1 ? 's' : ''}
                    </CandidateBadge>
                    <KeyboardArrowRightIcon />
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </Box>

    </Box>
  )
}